import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query"

import { readAuthSession } from "@/lib/auth-token"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import { AuthorSide, type FindingId, type Message, type MessageId } from "../data/review-data"

/**
 * One comment as `GET /comments` and `POST /comments` both return it.
 *
 * Exported because the send mutation reconciles its optimistic message against
 * the 201 body: the same wire shape must map through the same function, or the
 * message a sender sees changes the moment the server answers.
 */
export interface CommentWire {
  id: number
  review_id: number
  finding_id: number | null
  author_side: string
  author_id: number | null
  body: string
  created_at: string
  unread: boolean
}

interface ThreadWire {
  finding_id: number | null
  unread: number
  comments: CommentWire[]
}

interface ConversationOutWire {
  review_id: number
  side: string
  total_unread: number
  threads: ThreadWire[]
}

export interface ConversationData {
  messages: Message[]
  totalUnread: number
}

const AUTHOR_SIDES: readonly string[] = Object.values(AuthorSide)

function toAuthorSide(raw: string): AuthorSide {
  return AUTHOR_SIDES.includes(raw) ? (raw as AuthorSide) : AuthorSide.REVIEWER
}

// Names are intentionally role-only on the wire (DD-16); real identities
// live in the People sidebar.
export function authorNameFor(side: AuthorSide): string {
  if (side === AuthorSide.AI) return "Pyxis AI"
  if (side === AuthorSide.CLIENT) return "Client"
  return "Reviewer"
}

/**
 * The signed-in user's numeric id, for deciding which messages are their own.
 *
 * Read from the session store rather than passed in: the mapping below runs
 * inside a query function, where no React context is available.
 */
export function currentUserId(): number | null {
  const session = readAuthSession()
  if (!session) return null
  const parsed = Number(session.user.id)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Wire comment → the shape the panel renders.
 *
 * Exported for the send mutation, which maps the 201 body through this same
 * function. Two mappings would drift, and the drift would show as a message
 * changing appearance the instant it was confirmed.
 */
export function toMessage(c: CommentWire, myUserId: number | null): Message {
  const side = toAuthorSide(c.author_side)
  return {
    id: String(c.id) as MessageId,
    authorName: authorNameFor(side),
    authorSide: side,
    body: c.body,
    at: c.created_at,
    findingId: c.finding_id != null ? (String(c.finding_id) as FindingId) : null,
    mine: c.author_id != null && myUserId != null && c.author_id === myUserId,
    unread: c.unread,
  }
}

async function fetchComments(reviewId: string, signal: AbortSignal): Promise<ConversationData> {
  const wire = await http.get<ConversationOutWire>(
    `/api/v1/reviews/${encodeURIComponent(reviewId)}/comments`,
    { signal },
  )
  const my = currentUserId()
  const messages: Message[] = []
  for (const thread of wire.threads) {
    for (const comment of thread.comments) {
      messages.push(toMessage(comment, my))
    }
  }
  messages.sort((a, b) => a.at.localeCompare(b.at))
  return { messages, totalUnread: wire.total_unread }
}

/**
 * Messages this client is still sending, or failed to send.
 *
 * A fetch answers with what the server holds, and a message mid-flight is not
 * in it — so replacing the cache wholesale would drop a `failed` row and take
 * the typed text with it. Any live frame, window focus or reconnect is enough
 * to trigger that, which would undo the whole point of keeping failed sends on
 * screen. They are re-appended until the server knows them.
 */
export function localOnly(held: ConversationData | undefined, served: Message[]): Message[] {
  if (!held) return []
  const known = new Set(served.map((m) => m.id))
  return held.messages.filter((m) => m.status !== undefined && !known.has(m.id))
}

export function useReviewCommentsQuery(reviewId: string | null): UseQueryResult<ConversationData> {
  const qc = useQueryClient()
  return useQuery({
    queryKey: reviewId != null ? qk.reviews.comments(reviewId) : ["reviews", "comments", "none"],
    queryFn: async ({ signal, queryKey }) => {
      const served = await fetchComments(reviewId as string, signal)
      const pending = localOnly(qc.getQueryData<ConversationData>(queryKey), served.messages)
      if (pending.length === 0) return served
      return { ...served, messages: [...served.messages, ...pending] }
    },
    enabled: reviewId != null,
  })
}
